import { useEffect, useState } from "react";
import { RefreshCw, Trash2, Copy, CheckCircle2, XCircle, Bell } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const KIND_META = {
  "notify": { label: "Уведомление (IPN)", icon: Bell, cls: "bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30" },
  "return-success": { label: "Возврат — успех", icon: CheckCircle2, cls: "bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30" },
  "return-failure": { label: "Возврат — отказ", icon: XCircle, cls: "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30" },
};

const PARTS = [
  { key: "query", label: "Параметры URL" },
  { key: "form", label: "Form-body" },
  { key: "json", label: "JSON-body" },
];

const KV = ({ obj }) => {
  if (!obj || (typeof obj === "object" && Object.keys(obj).length === 0)) {
    return <div className="text-xs text-muted-foreground italic">пусто</div>;
  }
  if (typeof obj !== "object") return <div className="text-xs">{String(obj)}</div>;
  return (
    <div className="space-y-1">
      {Object.entries(obj).map(([k, v]) => (
        <div key={k} className="flex flex-wrap items-start gap-2 text-xs">
          <span className="font-mono font-semibold text-muted-foreground">{k}:</span>
          <span className="font-mono break-all">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
        </div>
      ))}
    </div>
  );
};

export default function AdminPayments() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/payment/events");
      setEvents(data);
    } catch {
      toast.error("Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Удалить событие?")) return;
    try { await api.delete(`/payment/events/${id}`); toast.success("Удалено"); load(); }
    catch { toast.error("Не удалось удалить"); }
  };

  const clearAll = async () => {
    if (!window.confirm("Удалить ВСЕ события? Это нельзя отменить.")) return;
    try { await api.delete("/payment/events"); toast.success("Очищено"); load(); }
    catch { toast.error("Не удалось очистить"); }
  };

  const externalBase = (typeof window !== "undefined" ? window.location.origin : "");
  const urls = {
    notify: `${externalBase}/api/payment/notify`,
    success: `${externalBase}/api/payment/return-success`,
    failure: `${externalBase}/api/payment/return-failure`,
  };
  const copy = (text) => {
    try { navigator.clipboard.writeText(text); toast.success("Скопировано"); }
    catch { toast.error("Не получилось скопировать"); }
  };

  const visible = filter === "all" ? events : events.filter((e) => e.kind === filter);

  return (
    <div className="max-w-5xl w-full">
      <div className="mb-6 sm:mb-8">
        <div className="text-xs font-semibold uppercase tracking-wider text-accent mb-2">Платежи</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Платёжные уведомления</h1>
        <p className="text-sm text-muted-foreground mt-2">URL-адреса для подключения платёжки и журнал всех входящих запросов от неё.</p>
      </div>

      {/* URLs to give to the payment provider */}
      <div className="grid sm:grid-cols-3 gap-3 mb-6" data-testid="payment-urls-grid">
        {[
          { key: "notify", label: "URL оповещения (IPN)", hint: "Адрес, на который платёжка POST-ит статус", url: urls.notify },
          { key: "success", label: "URL успешной оплаты", hint: "Куда вернётся клиент после оплаты", url: urls.success },
          { key: "failure", label: "URL возврата при отказе", hint: "Куда вернётся клиент при ошибке/отмене", url: urls.failure },
        ].map((u) => (
          <div key={u.key} className="p-4 rounded-2xl border border-border bg-card">
            <div className="text-xs font-semibold text-muted-foreground mb-1">{u.label}</div>
            <div className="text-[11px] text-muted-foreground mb-2">{u.hint}</div>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={u.url}
                onClick={(e) => e.target.select()}
                className="flex-1 min-w-0 h-9 px-3 rounded-lg border border-input bg-background text-xs font-mono"
                data-testid={`payment-url-${u.key}`}
              />
              <button
                onClick={() => copy(u.url)}
                className="size-9 rounded-lg border border-border flex items-center justify-center hover:bg-muted"
                title="Скопировать"
                data-testid={`payment-url-copy-${u.key}`}
              >
                <Copy className="size-3.5" />
              </button>
            </div>
            <div className="text-[10px] text-muted-foreground mt-2">Методы: GET и POST</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {[
          { k: "all", label: "Все" },
          { k: "notify", label: "Уведомления" },
          { k: "return-success", label: "Успех" },
          { k: "return-failure", label: "Отказ" },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${filter === f.k ? "bg-foreground text-background border-foreground" : "border-border hover:bg-muted"}`}
          >
            {f.label}
          </button>
        ))}
        <div className="flex-1" />
        <button onClick={load} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-border text-xs font-medium hover:bg-muted">
          <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} /> Обновить
        </button>
        {events.length > 0 && (
          <button onClick={clearAll} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-destructive/30 text-destructive text-xs font-medium hover:bg-destructive hover:text-destructive-foreground">
            <Trash2 className="size-3.5" /> Очистить
          </button>
        )}
      </div>

      {visible.length === 0 ? (
        <div className="py-16 text-center text-sm text-muted-foreground border border-dashed border-border rounded-3xl">
          Пока нет событий. Они появятся, как только платёжная система начнёт отправлять данные.
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((ev) => {
            const meta = KIND_META[ev.kind] || { label: ev.kind, icon: Bell, cls: "bg-muted text-muted-foreground border-border" };
            const Icon = meta.icon;
            return (
              <div key={ev.id} className="p-4 sm:p-5 rounded-2xl border border-border bg-card">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <div className="size-10 rounded-xl bg-muted flex items-center justify-center">
                      <Icon className="size-5" />
                    </div>
                    <div>
                      <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${meta.cls}`}>
                        {meta.label}
                      </span>
                      <span className="ml-2 inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold bg-muted">{ev.method}</span>
                      <div className="text-xs text-muted-foreground mt-1">
                        {new Date(ev.created_at).toLocaleString("ru-RU")} · IP: {ev.client_ip || "—"}
                      </div>
                    </div>
                  </div>
                  <button onClick={() => remove(ev.id)} className="size-8 rounded-md text-destructive hover:bg-destructive hover:text-destructive-foreground" title="Удалить">×</button>
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                  {PARTS.map((p) => (
                    <div key={p.key} className="p-3 rounded-xl bg-muted/40 min-w-0">
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">{p.label}</div>
                      <KV obj={ev[p.key]} />
                    </div>
                  ))}
                </div>
                {ev.raw_body && (
                  <details className="mt-3">
                    <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">Сырое тело запроса</summary>
                    <pre className="mt-2 p-3 rounded-xl bg-muted/40 text-[11px] overflow-x-auto whitespace-pre-wrap break-all">{ev.raw_body}</pre>
                  </details>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
